import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';
import { examsSelectors } from '../slices/exams/examsSlice';
import { selectRctExam } from '../services/exams';
import { updateCurrentUserInfo } from '../services/auth';
import CustomModal from '../components/Global/CustomModal';
import RctTimeline from '../assets/rct/rct-timeline.png';
import OraSwitch from '../components/CustomMUI/Switch';
import Tooltip from '@mui/material/Tooltip';

interface UseRctSelectorProps {
  refreshHomePage: () => Promise<void>;
}

export const useRctSelector = ({ refreshHomePage }: UseRctSelectorProps) => {
  const currentUser = useSelector((state: RootState) => state.user.profile);
  const primaryExam = useSelector(examsSelectors.selectPrimaryExam);
  const [showRctSelector, setShowRctSelector] = useState(false);
  const [acceptedConsent, setAcceptedConsent] = useState(false);

  const consentRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    if (primaryExam?.isRCT && !currentUser?.onboardingTips['rct-selector']) {
      setShowRctSelector(true);
    }
  }, [primaryExam?.id, currentUser?.onboardingTips]);

  const handleRctExamSelection = async (
    examId: string,
    choice: 'step1' | 'step2'
  ) => {
    if (!currentUser) {
      setShowRctSelector(false);
      return;
    }
    try {
      await selectRctExam(examId, choice);
      await refreshHomePage();
      await updateCurrentUserInfo({
        onboardingTips: {
          ...currentUser?.onboardingTips,
          'rct-selector': true,
        },
      });
    } catch (error) {
      console.error('Error handling RCT exam selection:', error);
    } finally {
      setShowRctSelector(false);
    }
  };

  const determineShake = () => {
    if (consentRef.current && !acceptedConsent) {
      consentRef.current.classList.add('shake');
      setTimeout(() => {
        consentRef.current?.classList.remove('shake');
      }, 500);
    }
  };

  const renderRctSelector = () => {
    if (!primaryExam?.isRCT || !showRctSelector) return null;
    return (
      <CustomModal
        open={showRctSelector}
        onClose={() => {}}
        name="rct-selector"
        disableBackdropClick={true}
      >
        <div className="rct-selector-modal">
          <div className="modal_header">
            <h2 className="m-t-0">Choose Your Exam</h2>
          </div>
          <div className="modal_content">
            <div className="rct-consent-section">
              <p>
                To participate in this study, you must read and consent to the{' '}
                <a
                  href="https://ora-misc-assets.s3.us-west-1.amazonaws.com/Final_RCT_Consent.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="consent-link"
                >
                  terms of this reaseach study.
                </a>
              </p>
              <div className="rct-consent-switch" ref={consentRef}>
                <OraSwitch
                  checked={acceptedConsent}
                  onChange={(e) => setAcceptedConsent(e.target.checked)}
                />
                <label htmlFor="acceptedConsent">
                  I have read the terms and consent to participate.
                </label>
              </div>
            </div>
            <div className="rct-timeline">
              <img src={RctTimeline} alt="RCT Timeline" />
            </div>
            <p>Select which USMLE Step exam you're preparing for:</p>
            <Tooltip
              title={
                acceptedConsent
                  ? ''
                  : 'You must consent to participate in the study to select an exam.'
              }
              enterTouchDelay={0}
            >
              <div
                className="rct-selector-options"
                onMouseEnter={determineShake}
              >
                <button
                  type="button"
                  className="button button--glass rct-option-button step1"
                  onClick={() => {
                    const step1PreAssessmentId =
                      '211aa787-bc1b-4258-a152-9ec85c91aedd';
                    handleRctExamSelection(step1PreAssessmentId, 'step1');
                  }}
                  disabled={!acceptedConsent}
                >
                  <h3>USMLE Step 1</h3>
                </button>
                <button
                  type="button"
                  className="button button--glass rct-option-button step2"
                  onClick={() => {
                    const step2PreAssessmentId =
                      '803a6ed3-ad0e-4813-9921-b11181344b95';
                    handleRctExamSelection(step2PreAssessmentId, 'step2');
                  }}
                  disabled={!acceptedConsent}
                >
                  <h3>USMLE Step 2</h3>
                </button>
              </div>
            </Tooltip>
          </div>
        </div>
      </CustomModal>
    );
  };

  return {
    renderRctSelector,
    showRctSelector,
  };
};
